import moment from 'moment';
import {
  TransferMacrostatus,
  TransferMicroStatusWorkflow,
  TransferMicrostatus,
  TransferSourceEnum,
  TransferStatusWorkflow,
  TransferTypes
} from './types';
import * as XLSX from 'xlsx-js-style';
import TransferReportBuilder, {
  TransferReport
} from '@/model/builders/ffl-transfer-excel.mjs';
import { FFLListTransfer } from '@/model/builders/ffl-boa-get-transformer.mjs';
import { FilterModel } from 'ag-grid-community';
import {
  getFormattedDate,
  getFormattedDateTime,
  getFormattedDateTimeV2
} from './elements/value-formatters/date-formatter.mjs';
import {
  EnumTransferMessageType,
  pastTenseFailedStatus,
  pastTenseStatus,
  stepMapping,
  transferDetailsMicroStatus,
  transferMacrostatus,
  transferMicrostatus,
  transferQueueSubStatusV2,
  transferType
} from '@/utils/constants';
import { keyType } from '@/queue/types';
import { StoreNameType } from './ducks/slice';
import { ContactDetail } from '@masterffl/dealer-detail-component/dist/utils/types';
import momentTz from 'moment-timezone';

export const formatTransferStatusList = (
  data?: Array<TransferStatusWorkflow>
) => {
  if (!data || data.length <= 0) return [];
  return data.sort((a, b) =>
    a.order > b.order ? 1 : b.order > a.order ? -1 : 0
  );
};

export const exportXLSXFile = (
  data: FFLListTransfer,
  filters: FilterModel,
  generatedBy: string,
  isArchive: boolean,
  storeData: StoreNameType[],
  isGlobalAdmin: boolean
) => {
  try {
    let response = TransferReportBuilder.buildTransferReportList(data);
    response = updateResponse(response, isGlobalAdmin);
    if (!isArchive)
      response = response.map((ele) => {
        delete ele['Archive Date'];
        return ele;
      });
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([[]]);

    // Add the single object to the first cell
    const filtersData = formatXLSXFilter(filters, generatedBy, storeData);

    // Convert the object to a formatted string with each key-value pair on a new line
    const formattedString = Object.entries(filtersData)
      .map(([key, value]) => {
        let formattedValue: any = value;
        if (key === 'Status') {
          formattedValue = formattedValue.split(', ');
          formattedValue = formattedValue
            .map(
              (ele: string) =>
                transferMacrostatus[ele as keyType<typeof transferMacrostatus>]
            )
            .join(', ');
        } else if (key === 'Sub-Status') {
          formattedValue = formattedValue.split(', ');
          formattedValue = formattedValue
            .map(
              (ele: string) =>
                transferMicrostatus[ele as keyType<typeof transferMicrostatus>]
            )
            .join(', ');
        }
        return `${key}: ${formattedValue}`;
      })
      .join(' \n');
    XLSX.utils.sheet_add_aoa(worksheet, [[formattedString]], { origin: 'A1' });
    XLSX.utils.sheet_add_aoa(worksheet, [[]], { origin: 'A2' });

    // Add the array of objects starting from the second row
    XLSX.utils.sheet_add_json(worksheet, response, { origin: 'A3' });

    // Calculate column widths
    const columnWidths = response.reduce((acc: any, row: any) => {
      Object.keys(row).forEach((key: string) => {
        acc[key] = Math.max(acc[key] || 0, String(row[key]).length);
      });
      return acc;
    }, {});

    // Apply column widths to the worksheet
    worksheet['!cols'] = Object.keys(columnWidths).map((key) => ({
      wch: columnWidths[key] + 8
    }));
    worksheet['!rows'] = worksheet['!rows'] || [];

    // Make all cells in the second row bold
    const range = XLSX.utils.decode_range(worksheet['!ref'] as string);
    const affectedRows = [0, 2];
    for (const row of affectedRows) {
      const style =
        row !== 0
          ? { font: { bold: true } }
          : { alignment: { wrapText: true } };
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        // Initialize the cell if it doesn't exist
        if (!worksheet[cellAddress]) {
          worksheet[cellAddress] = { t: 's', v: '' }; // Assuming the cell is a string type
        }
        worksheet[cellAddress].s = style;
      }
    }

    // Set the height of the first row
    worksheet['!rows'][0] = { hpx: calculateRowHeight(formattedString) };
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const currentDate = new Date();
    const formattedDate = moment(currentDate).format('MM/DD/YYYY_HH_mm_ss');
    const filename = `Transfers_export_ ${formattedDate}.xlsx`;
    XLSX.writeFile(workbook, filename);
  } catch (e) {
    console.log(e);
  }
};

export const getFormattedTimeFromNow = (date: Date) => {
  return moment(date).fromNow();
};

export const formatXLSXFilter = (
  filters: FilterModel,
  generatedBy: string,
  storeData: StoreNameType[]
) => {
  const formattedFilters: any = {};
  Object.keys(filters).map((filterKey) => {
    if (
      filters[filterKey]?.['values']?.length ||
      filters[filterKey]?.filter ||
      filters[filterKey]?.dateFrom
    ) {
      const filterValue =
        filters[filterKey]?.['values'] && filters[filterKey]?.['values'].length
          ? filters[filterKey]?.['values']?.join(', ')
          : filters[filterKey]?.filter;
      if (filterKey === 'storeName') {
        const foundItem = filters.storeName.values.map((val: string) =>
          storeData.find((item) => item.value === val)
        );
        const StoreValue = foundItem.map((item: StoreNameType) => item.label);
        formattedFilters['Store Name'] = StoreValue.join(', ');
      } else if (filterKey === 'transferType') {
        formattedFilters[
          mapFilterName[filterKey as keyType<typeof mapFilterName>]
        ] = transferType[filterValue as keyof TransferTypes];
      } else if (filterKey === 'createdAt') {
        formattedFilters[
          mapFilterName[filterKey as keyType<typeof mapFilterName>]
        ] = `${getFormattedDate(
          filters[filterKey]?.dateFrom.split(' ')[0]
        )} - ${getFormattedDate(filters[filterKey]?.dateTo.split(' ')[0])}`;
      } else {
        formattedFilters[
          mapFilterName[filterKey as keyType<typeof mapFilterName>]
        ] = filterValue;
      }
    }
  });
  formattedFilters['Generated'] = getFormattedDateTime(new Date());
  formattedFilters['Generated by'] = generatedBy;
  return formattedFilters;
};

const mapFilterName = {
  derivedStatus: 'Status',
  microStatus: 'Sub-Status',
  agent: 'Agent',
  searchText: 'Search',
  storeName: 'Store Name',
  source: 'Source',
  transferType: 'Transfer Type',
  createdAt: 'Transfer Initiated'
};


function calculateRowHeight(content: string) {
  const defaultRowHeight = 25; // Default row height in pixels
  const lineHeight = 1.76; // Line height factor
  const lines = content.split(' \n');
  const contentHeight = lines.length * lineHeight * defaultRowHeight; // Calculate content height
  return contentHeight;
}

export function reverseArr(arrayList: Array<any>) {
  var ret = new Array();
  for (var i = arrayList.length - 1; i >= 0; i--) {
    ret.push(arrayList[i]);
  }
  return ret;
}

export function trimDate(date: string | undefined) {
  if (!date) return null;
  return moment(date).local().format('MM/DD/YYYY');
}

export function fixTimezoneOffset(date: Date | string | undefined) {
  if (!date) return null;
  if (!isNaN(Number(date)) && Number(date) > 0 && moment.unix(Number(date)).isValid()) {
    const dateformat = new Date(date)
    date = dateformat.toLocaleString('en-US');
  }
  date = date.toLocaleString('en-US');
  const modifyDate = moment(date).local()?.toDate();
  return modifyDate;
}

function updateResponse(data: TransferReport[], isGlobalAdmin: boolean) {
  return data.map((obj: TransferReport) => {
    if (isGlobalAdmin) delete obj['Transfer Completed'];
    else {
      delete obj['Inbound Transfer Complete'];
      delete obj['Ready for Outbound Processing'];
    }
    return obj;
  });
}

function getSourceTextForUI(source: string | undefined, type: string | undefined) {
  switch (source) {
    case 'ffl360':
      return type !== undefined && type.toLowerCase() === 'cfd' ? 'added via profile' :'uploaded via profile';
    case 'upload_ui':
      return type !== undefined && type.toLowerCase() === 'cfd' ? 'added during transfer'  : 'uploaded during transfer';
    case 'backend':
      return 'added via backend';
    case 'email_campaign':
      return 'added by dealer via Document UI';
    case 'myffl':
      return type !== undefined && type.toLowerCase() === 'cfd' ? 'added via MyFFL' :'uploaded via MyFFL';
  }
  return 'added via backend';
}

function formatResumeReasonLabel(reason?: string) {
  if (!reason) return '';
  const reasons: Record<string, string> = {
    dealer_not_opted_in: 'Dealer not opt-in',
    required_documents_not_available: 'Required document not available',
    required_documents_missing: 'Required document not available',
    ez_check_failed: 'Ez-check failed'
  };
  if (reasons[reason]) return reasons[reason];
  return reason
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function getFormattedStatus(
  status: string,
  step: TransferMicroStatusWorkflow
) {

  if (
    status ===
    transferDetailsMicroStatus[
    TransferMicrostatus.in_hold as keyType<
      typeof transferDetailsMicroStatus
    >
    ]
  ) {
    if (step?.metaData?.reason && step?.metaData?.reason?.status) {
      const holdReasonStatus = transferDetailsMicroStatus[step?.metaData?.reason?.status as keyType<typeof transferDetailsMicroStatus>];
      const reasonStatusText = `: ${holdReasonStatus}`
      const reasonStep = step?.metaData?.reason
      let formattedTime = ''
      if (reasonStep && reasonStep?.metaData && reasonStep?.metaData?.schedulerTimeout && reasonStep?.metaData?.schedulerId) {
        formattedTime = `: ${showRemainingTime(reasonStep)}`;
      }
      return `On Hold ${reasonStatusText} ${formattedTime}`
    }
    return 'On Hold';
  }

  if (
    status ===
    transferDetailsMicroStatus[
    TransferMicrostatus.accepted_transfer as keyType<
      typeof transferDetailsMicroStatus
    >
    ]
  ) {

    const optinMapping = {
      order: "Opted in for this transfer",
      store: "Opted in for this store",
      all: "Opted in for all stores",
    };
    const optinMappingOld: any = {
      opt_in_all: 'all stores',
      opt_in_store: 'this store',
      opt_in_order: 'this transfer'
    };

    const optinValue = step?.metaData?.optChoice as keyType<typeof optinMapping>;
    const optinOldValue = step?.metaData?.value as keyType<typeof optinMapping>;
    if (optinMappingOld && optinMappingOld[optinOldValue]) {
      return status.replace(')', ` ${optinMappingOld[optinOldValue]})`);
    }

    const actionPerformedBy: any = {
      System: 'backend',
      Agent: 'profile',
      Dealer: 'dealer',
      system: 'backend',
      agent: 'profile',
      dealer: 'dealer',
      Support: 'profile',
      support: 'profile',
      SMS: 'dealer',
      sms: 'dealer',
      email_campaign: 'email'
    }

    if (optinValue && optinMapping[optinValue]) {
      const performer = step?.metaData?.actionPerformedBy ? step?.metaData?.actionPerformedBy : (step?.metaData?.createdby || step?.metaData?.createdBy);
      if (performer && actionPerformedBy[performer]) {
        if (performer === 'Dealer' || performer === 'dealer') {
          return `Opted in by dealer during transfer`;
        } else {
          const emailText = (step?.metaData?.email && (performer === 'Agent' || performer === 'agent')) ? `(${step?.metaData?.email})` : '';
          const userId = step?.metaData?.createdby && ((performer === 'Agent' || performer === 'agent') || (performer === 'Support' || performer === 'support')) ? `(${step?.metaData?.createdby})` : '';
          if(step?.metaData?.actionPerformedBy === 'email_campaign'){
            return `Opted in by dealer via Document UI ${emailText}`
          }
          return `Opted in via ${actionPerformedBy[performer]} ${userId} ${emailText} `;

        }
      }
    } else {
      return 'Accepted Transfer';
    }

  }
  if (
    status ===
    transferDetailsMicroStatus[
    TransferMicrostatus.opted_out as keyType<typeof transferDetailsMicroStatus>
    ]
  ) {
    const optoutMappingOld = {
      all: 'all store',
      store: 'this store',
      order: 'this transfer'
    };

    const optoutValueOld = step?.metaData?.value as keyType<typeof optoutMapping>;
    if (optoutValueOld && optoutMappingOld[optoutValueOld]) {
      return status + ` (${optoutMappingOld[step.metaData?.value as keyType<typeof optoutMappingOld>]})`;
    }

    const optoutMapping = {
      order: "Opted out for this transfer",
      store: "Opted out for this store",
      all: "Opted out for all store",
    };

    const actionPerformedBy: any = {
      System: 'backend',
      Agent: 'profile',
      Dealer: 'dealer',
      system: 'backend',
      agent: 'profile',
      dealer: 'dealer',
      SMS: 'SMS',
      sms: 'dealer',
      Support: 'profile',
      support: 'profile',
      email_campaign: 'email'
    }

    const optoutValue = step?.metaData?.optChoice as keyType<typeof optoutMapping>;
    if (optoutValue && optoutMapping[optoutValue]) {
      if (step?.metaData?.actionPerformedBy && actionPerformedBy[step?.metaData?.actionPerformedBy]) {
        if (step?.metaData?.actionPerformedBy === 'Dealer') {
          return `Opted out by dealer during transfer`;
        } else {
          const emailText = step?.metaData?.email && step?.metaData?.actionPerformedBy === 'Agent' ? `(${step?.metaData?.email})` : '';
          const userId = step?.metaData?.createdby && (step?.metaData?.actionPerformedBy === 'Agent' || step?.metaData?.actionPerformedBy === 'Support'  )  ? `(${step?.metaData?.createdby})` : '';
          if(step?.metaData?.actionPerformedBy === 'email_campaign'){
            return `Opted out by dealer via Document UI ${emailText}`
          }
          return `Opted out via ${actionPerformedBy[step?.metaData?.actionPerformedBy]} ${userId} ${emailText}`;
        }
      }
    }

    return status;
  }
  if (
    status ===
    transferDetailsMicroStatus[
    TransferMicrostatus.ez_check as keyType<
      typeof transferDetailsMicroStatus
    >
    ] || status === pastTenseStatus[
    TransferMicrostatus.ez_check as keyType<
      typeof transferDetailsMicroStatus
    >
    ]
  ) {
    const ezCheckStatus = {
      in_progress: 'FFL eZ check running',
      false: 'FFL eZ check - failed:',
      failed: 'FFL eZ check - failed:',
      true: 'FFL eZ check - passed:',
      passed: 'FFL eZ check - passed:'
    };
    if (step?.metaData && 'status' in step?.metaData && ezCheckStatus[step?.metaData?.status as keyType<typeof ezCheckStatus>])
      return ezCheckStatus[step?.metaData?.status as keyType<typeof ezCheckStatus>];
  }
  if (
    status ===
    transferDetailsMicroStatus[
    TransferMicrostatus.sot_tax_stamp_validated as keyType<
      typeof transferDetailsMicroStatus
    >
    ] || status === pastTenseStatus[
    TransferMicrostatus.sot_tax_stamp_validated as keyType<
      typeof transferDetailsMicroStatus
    >
    ]
  ) {
    if (typeof step?.metaData?.approved === "boolean" && step?.metaData?.reviewed_by) {
      return step?.metaData?.approved ? `SOT Tax Stamp approved by ${step?.metaData?.reviewed_by || ""}` : `SOT Tax Stamp rejected by ${step?.metaData?.reviewed_by || ""}`
    }
    else if (status === pastTenseStatus[
      TransferMicrostatus.sot_tax_stamp_validated as keyType<
        typeof transferDetailsMicroStatus
      >
    ]) {
      return step?.metaData?.approved ? transferDetailsMicroStatus[
        TransferMicrostatus.sot_tax_stamp_validated as keyType<
          typeof transferDetailsMicroStatus
        >
      ] : pastTenseFailedStatus[
      TransferMicrostatus.sot_tax_stamp_validated as keyType<
        typeof transferDetailsMicroStatus
      >
      ]
    }
  }
  if (
    status ===
    transferDetailsMicroStatus[
    TransferMicrostatus.loa_certificate_validated as keyType<
      typeof transferDetailsMicroStatus
    >
    ] || status === pastTenseStatus[
    TransferMicrostatus.loa_certificate_validated as keyType<
      typeof transferDetailsMicroStatus
    >
    ]
  ) {
    if (typeof step?.metaData?.approved === "boolean" && step?.metaData?.reviewed_by) {
      return step?.metaData?.approved ? `LOA Certificate approved by ${step?.metaData?.reviewed_by || ""}` : `LOA Certificate rejected by ${step?.metaData?.reviewed_by || ""}`
    }
    else if (status === pastTenseStatus[
      TransferMicrostatus.loa_certificate_validated as keyType<
        typeof transferDetailsMicroStatus
      >
    ]) {
      return step?.metaData?.approved ? transferDetailsMicroStatus[
        TransferMicrostatus.loa_certificate_validated as keyType<
          typeof transferDetailsMicroStatus
        >
      ] : pastTenseFailedStatus[
      TransferMicrostatus.loa_certificate_validated as keyType<
        typeof transferDetailsMicroStatus
      >
      ]
    }
  }
  if (
    status ===
    transferDetailsMicroStatus[
    TransferMicrostatus.ocr_checks as keyType<
      typeof transferDetailsMicroStatus
    >
    ] || status === pastTenseStatus[
    TransferMicrostatus.ocr_checks as keyType<
      typeof transferDetailsMicroStatus
    >
    ]
  ) {
    const ocrCheckStatus = {
      in_progress: 'Running OCR / Sanity Checks',
      failed: 'OCR / Sanity Checks',
      passed: 'OCR / Sanity Checks'
    };
    if (step?.metaData?.status && ocrCheckStatus[step?.metaData?.status as keyType<typeof ocrCheckStatus>]) {
      if (step?.metaData?.status === "failed" && step?.metaData?.code)
        return `${ocrCheckStatus[step?.metaData?.status as keyType<typeof ocrCheckStatus>]}(${stepMapping[step?.metaData?.code]})`;
      if (["in_progress", "passed"].includes(step?.metaData?.status as keyType<typeof ocrCheckStatus>)) {
        const fileType = step?.metaData?.documentType ? `${(step?.metaData?.documentType)?.toUpperCase()} - ` : '';
        return `${fileType} ${ocrCheckStatus[step?.metaData?.status as keyType<typeof ocrCheckStatus>]} `;
      }
    }
  }
  if (step?.status === TransferMicrostatus.manual_license_review && typeof step?.metaData?.status === "boolean") {
    if (step?.metaData?.reviewed_by)
      return step?.metaData?.status ? `${(step?.metaData?.documentType)?.toUpperCase()} - manually approved (${step?.metaData?.reviewed_by || ""})` : `${(step?.metaData?.documentType)?.toUpperCase()} - manually rejected (${step?.metaData?.reviewed_by || ""})`;
    else
      return step?.metaData?.status ? `${(step?.metaData?.documentType)?.toUpperCase()} - manually approved` : `${(step?.metaData?.documentType)?.toUpperCase()} manually rejected`;
  }
  if (step?.status === TransferMicrostatus.manual_review_pending) {
    return step?.metaData?.documentType ? `${(step?.metaData?.documentType)?.toUpperCase()} - manual review requested` : 'Manual review requested';
  }
  const actionPerformedBy: any = {
    System: 'backend',
    Agent: 'profile',
    Dealer: 'dealer',
    system: 'backend',
    agent: 'profile',
    dealer: 'dealer',
    Support: 'profile',
    support: 'profile',
    SMS: 'dealer',
    sms: 'dealer',
    email_campaign: 'email',
    ffl360: 'profile',
    myffl: 'MyFFL'
  }
  if (step?.status === TransferMicrostatus.document_available) {
    const source = getSourceTextForUI(step?.metaData?.source, step?.metaData?.documentType);
    const email = step?.metaData?.email ? `(${step?.metaData?.email})` : '';
    if(step?.metaData?.documentType && step?.metaData?.documentType.toLowerCase() === "cfd"){
      if(step?.metaData?.source?.toLowerCase() === "email_campaign"){
        return step?.metaData?.documentType ? `CFD# added by dealer via Document UI ${email}` : 'CFD# available';
      }
      if(step?.metaData?.source?.toLowerCase() === "dealer" || step?.metaData?.source?.toLowerCase() === "upload_ui"){
        return step?.metaData?.documentType ? `CFD# added during transfer ${email}` : 'CFD# available';
      }
      if(step?.metaData?.source){
        return step?.metaData?.documentType ? `CFD# added via ${actionPerformedBy[step?.metaData?.source]} ${email}` : 'CFD# available';
      }
      return 'CFD# added via backend';
    }
    return step?.metaData?.documentType ? `${(step?.metaData?.documentType)?.toUpperCase()} ${source} ${email}` : 'Document available';
  }
  if (step?.status === TransferMicrostatus.document_uploaded) {
    return step?.metaData?.documentType ? `${(step?.metaData?.documentType)?.toUpperCase()} - uploaded during transfer` : 'Document uploaded during transfer';
  }
  if (status === transferDetailsMicroStatus[TransferMicrostatus.dealer_selected as keyType<typeof transferDetailsMicroStatus>]) {
    return step?.metaData?.email ? `Dealer reassigned: by ${step?.metaData?.createdBy === "Customer" ? "customer" : "agent"} ${step?.metaData?.email ? `(${step?.metaData?.email})` : ''}` : 'Dealer assigned';
  }
  if (status === transferDetailsMicroStatus[TransferMicrostatus.customer_selected as keyType<typeof transferDetailsMicroStatus>]) {
    return step?.metaData?.email ? `Dealer reassigned: by customer ${step?.metaData?.email || ''}` : status;
  }
  if (status === transferDetailsMicroStatus[TransferMicrostatus.resent_email as keyType<typeof transferDetailsMicroStatus>]) {
    return step?.metaData?.email ? `Take action email to dealer by user ${step?.metaData?.email ? `(${step?.metaData?.email})` : ''}` : `Take action email to dealer by user`;
  }
  const emailStatus = ['Email Delivered', 'Email Opened', 'Email Clicked'];
  if (emailStatus.includes(status)) {
    const emailText = step?.metaData?.email ? `(${step?.metaData?.email})` : '';
    return step?.metaData?.type ? `${EnumTransferMessageType[step?.metaData?.type]} ${emailText}` : status;
  }
  if (status === transferDetailsMicroStatus[TransferMicrostatus.email_bounced as keyType<typeof transferDetailsMicroStatus>]) {
    const emailText = step?.metaData?.email ? `(${step?.metaData?.email})` : '';
    return step?.metaData?.type ? `${EnumTransferMessageType[step?.metaData?.type]} ${emailText}` : status;
  }
  if (status === transferDetailsMicroStatus[TransferMicrostatus.email_unreachable as keyType<typeof transferDetailsMicroStatus>]) {
    const emailText = step?.metaData?.email ? `(${step?.metaData?.email})` : '';
    return emailText ? `Email - ${emailText} unreachable` : 'Email unreachable';
  }

  const actionPerformer: any = {
    Customer: 'customer',
    Agent: 'FFL agent',
    Dealer: 'dealer',
    Support: 'FFL support',
    SMS: 'SMS',
    ffl360: 'profile'
  }

  if (status === transferDetailsMicroStatus[TransferMicrostatus.email_acquired as keyType<typeof transferDetailsMicroStatus>]) {
    const email = step?.metaData?.email ? `(${step?.metaData?.email})` : '';
    if (step?.metaData?.actionPerformedBy === 'SMS' || step?.metaData?.actionPerformedBy === 'sms' || step?.metaData?.actionPerformedBy === 'Dealer' || step?.metaData?.actionPerformedBy === 'dealer'){
      return step?.metaData?.actionPerformedBy ? `Email - ${email} acquired via SMS by the dealer ` : `Email acquired`;
    } 
    if (step?.metaData?.actionPerformedBy === 'Customer'){
      return step?.metaData?.actionPerformedBy ? `Email - ${email} acquired via Email by the customer ` : `Email acquired`;
    }    
    return step?.metaData?.actionPerformedBy ? `Email - ${email} acquired by FFL360 user ${step?.metaData?.created_by ? `(${step?.metaData?.created_by}) `: ""}` : `Email acquired`;
  }
  if (status === transferDetailsMicroStatus[TransferMicrostatus.email_available as keyType<typeof transferDetailsMicroStatus>]) {
    const email = step?.metaData?.email ? `(${step?.metaData?.email})` : '';
    if (step?.metaData?.actionPerformedBy === 'SMS'){
      return step?.metaData?.actionPerformedBy ? `Email - ${email} acquired via SMS by the dealer ` : `Email acquired`;
    } 
    if (step?.metaData?.actionPerformedBy === 'Customer'){
      return step?.metaData?.actionPerformedBy ? `Email - ${email} acquired via Email by the customer ` : `Email acquired`;
    }    
    return step?.metaData?.actionPerformedBy ? `Email - ${email} acquired by FFL360 user ${step?.metaData?.created_by ? `(${step?.metaData?.created_by}) `: ""}` : `Email acquired`;
  }
  if (status === transferDetailsMicroStatus[TransferMicrostatus.dealer_documents_pending as keyType<typeof transferDetailsMicroStatus>]) {
    if(step?.metaData?.documentType && step?.metaData?.documentType.toLowerCase() === "cfd"){
      return 'Waiting for CFD#';
    }
    return status.replace("'NAME'", (step?.metaData?.documentType)?.toUpperCase() || '');
  }
  if (status === transferDetailsMicroStatus[TransferMicrostatus.skip_step_acquired as keyType<typeof transferDetailsMicroStatus>]) {
    const email = step?.metaData?.created_by ? `(${step?.metaData?.created_by})` : '';
    return `Step manually skipped by ${email} on`;
  }
  if (status === transferDetailsMicroStatus[TransferMicrostatus.document_validated as keyType<typeof transferDetailsMicroStatus>]) {
    const actionPerformedBy: any = {
      System: 'backend',
      Agent: 'profile',
      Dealer: 'dealer',
      system: 'backend',
      agent: 'profile',
      dealer: 'dealer',
      Support: 'profile',
      support: 'profile',
      SMS: 'dealer',
      sms: 'dealer',
      email_campaign: 'email',
      ffl360: 'profile',
      myffl: 'MyFFL',
    }
    if((step?.metaData?.documentType)?.toUpperCase() === 'CFD'){
      const email = step?.metaData?.email ? `(${step?.metaData?.email})` : '';
      if(step?.metaData?.source?.toLowerCase() === "email_campaign"){
        return step?.metaData?.documentType ? `CFD# added by dealer via Document UI ${email}` : 'CFD# available';
      }
      if(step?.metaData?.source?.toLowerCase() === "dealer" || step?.metaData?.source?.toLowerCase() === "upload_ui"){
        return step?.metaData?.documentType ? `CFD# added during transfer ${email}` : 'CFD# available';
      }
      if(step?.metaData?.source){
        return step?.metaData?.documentType ? `CFD# added via ${actionPerformedBy[step?.metaData?.source]} ${email}` : 'CFD# available';
      }
      return 'CFD# added via backend';
    }
    if (step?.metaData?.status) {
      return status.replace("'NAME'", (step?.metaData?.documentType)?.toUpperCase() || '');
    }
    return `${(step?.metaData?.documentType)?.toUpperCase()} - OCR / Sanity Check `;
  }

  if (step?.status === TransferMicrostatus.transfer_resume_attempted) {
    const base =
      transferDetailsMicroStatus[
        TransferMicrostatus.transfer_resume_attempted as keyType<
          typeof transferDetailsMicroStatus
        >
      ];
    const source = step?.metaData?.source;
    const reasonValues = Array.isArray(step?.metaData?.reasons)
      ? step.metaData.reasons
      : step?.metaData?.reason
        ? [step.metaData.reason]
        : [];
    const reasonText = reasonValues
      .map((reason: string) => formatResumeReasonLabel(reason))
      .filter(Boolean)
      .join(', ');

    if (source === 'cron') {
      return reasonText ? `${base} (scheduled) - Reason: ${reasonText}` : `${base} (scheduled)`;
    }
    if (source === 'manual') {
      return reasonText ? `${base} (manual) - Reason: ${reasonText}` : `${base} (manual)`;
    }
    return reasonText ? `${base} - Reason: ${reasonText}` : base;
  }

  return status;
}

export const getFormattedStatusOfBouncedEmail = (
  status: string,
  step: TransferMicroStatusWorkflow,
  microStatus: TransferMicroStatusWorkflow[] | undefined
) => {
  if (!Array.isArray(microStatus) || microStatus.length === 0) {
    return 'Micro status is not available';
  }

  const email = step.metaData?.email;

  const allEmailsSame = microStatus.every((ms) => ms.metaData?.email === email);

  const hasBouncedEmail = microStatus.some(
    (ms) =>
      ms.status ===
      transferMicrostatus[
      TransferMicrostatus.email_bounced as keyType<typeof transferMicrostatus>
      ]
  );

  const anyEmailDelivered = microStatus.some(
    (ms) =>
      ms.status ===
      transferMicrostatus[
      TransferMicrostatus.email_delivered as keyType<
        typeof transferMicrostatus
      >
      ]
  );

  const statusText =
    anyEmailDelivered && hasBouncedEmail && allEmailsSame
      ? 'Email Delivery Failed'
      : 'Email bounced';

  if (
    status ===
    transferMicrostatus[
    TransferMicrostatus.accepted_transfer as keyType<
      typeof transferMicrostatus
    >
    ]
  ) {

    const optinMapping = {
      opt_in_all: 'all store',
      opt_in_store: 'this store',
      opt_in_order: 'this transfer'
    };

    return optinMapping[step.metaData?.value as keyType<typeof optinMapping>]
      ? status.replace(')', ` ${statusText})`)
      : status;
  }
  if (
    status ===
    transferMicrostatus[
    TransferMicrostatus.opted_out as keyType<typeof transferMicrostatus>
    ]
  ) {
    const optoutMapping = {
      all: 'all store',
      store: 'this store',
      order: 'this transfer'
    };
    return optoutMapping[step.metaData?.value as keyType<typeof optoutMapping>]
      ? status + `(${statusText})`
      : status;
  }

  return status;
};

export const getPrimaryContactDetails = (contactDetails: ContactDetail[]) => {
  if (!contactDetails || contactDetails.length <= 0) return null;
  const primaryContactDetails = contactDetails.filter(
    (contact) => contact.isPrimary
  );
  return primaryContactDetails && primaryContactDetails.length > 0
    ? primaryContactDetails[0]
    : null;
};

export function getEightDigitLicenseKey(fullLicenseKey: string) {
  const first1 = fullLicenseKey?.substring(0, 1) || '';
  let second2 = fullLicenseKey?.substring(2, 4) || '';
  const last5 = fullLicenseKey?.substring(fullLicenseKey.length - 5) || '';
  if (fullLicenseKey && fullLicenseKey.length == 8) second2 = fullLicenseKey?.substring(1, 3) || '';
  return `${first1}${second2}${last5}`
}

// Format time function
function formatTime(diff: number, totalDuration: number): string {
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.ceil((diff % (1000 * 60)) / 1000);
  // const duration = totalDuration ? getFormattedDuration(totalDuration) : '';

  if (days > 0) {
    return `${days}d`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
}

const getFormattedDuration = (minutes: number) => {
  const days = Math.floor(minutes / (60 * 24));
  const hours = Math.floor((minutes % (60 * 24)) / 60);
  const mins = minutes % 60;

  if (days > 0) {
    return `/${days}d`;
  } else if (hours > 0) {
    return `/${hours}h ${mins}m`;
  } else {
    return `/${mins}m`;
  }
}

export function showRemainingTime(step: any) {
  const schedulerTimeout = step?.metaData?.schedulerTimeout;
  const totalDuration = step?.metaData?.totalDuration;
  const stoppedTime = step?.metaData?.stopped_at;

  if (!schedulerTimeout || step?.status === TransferMicrostatus.email_bounced){
     return `Email retry attempt scheduled on : ${getFormattedDateTimeV2(step?.metaData?.schedulerTimeout)}`;
  }

  const schedulerDate = new Date(schedulerTimeout);
  const currentDate = new Date();

  if (stoppedTime && !step?.metaData?.executed_at) {
    const stoppedDate = new Date(stoppedTime);
    const completedTime = schedulerDate.getTime() - stoppedDate.getTime();
    return completedTime ? `${formatTime(completedTime, totalDuration)} - Completed` : ''
  }

  // Calculate remaining time
  const remainingTime = schedulerDate.getTime() - currentDate.getTime();

  // If remainingTime is positive, show remaining time; otherwise, show expired message
  return remainingTime > 0
    ? `${formatTime(remainingTime, totalDuration)} - Remaining`
    : `0h 0m ${totalDuration ? getFormattedDuration(totalDuration) : ''} - Expired`;
}


export const getStepperStatusImg = (step: TransferStatusWorkflow, transferDetails: any) => {
  if (transferDetails && transferDetails?.subStatus && transferDetails?.subStatus === 'queue_status_on_hold' && step?.status === transferDetails.status) {
    return '/images/ui/failed-step.svg'
  }
  if (step?.status === TransferMacrostatus.waiting_dealer_action && step?.microStatus && step?.microStatus.length > 0) {
    const isOptedOut = step?.microStatus?.[0]?.status === TransferMicrostatus.opted_out;
    if (isOptedOut) {
      return '/images/ui/failed-step.svg'
    }
  }
  switch (step?.stepStatus) {
    case 'pending':
      return '/images/ui/gray-round-step.svg'
    case 'in_progress':
      return '/images/ui/black-step.svg'
    case 'success':
      return '/images/ui/success-step.svg'
    case 'failed':
      return '/images/ui/failed-step.svg'
    default:
      return '/images/ui/gray-round-step.svg'
  }
}


export const getStepImg = (step: TransferStatusWorkflow, transferDetails: any) => {
  const stepImgs: any =
  {
    transfer_initiated: {
      pending: '/images/ui/gray-rocket.svg',
      // in_progress: '/images/ui/yellow-rocket.svg',
      in_progress: '/images/ui/black-rocket.svg',
      success: '/images/ui/rocket.svg',
      failed: '/images/ui/red-rocket.svg'
    },
    new_dealer_assigned: {
      pending: '/images/ui/gray-rocket.svg',
      // in_progress: '/images/ui/yellow-rocket.svg',
      in_progress: '/images/ui/black-rocket.svg',
      success: '/images/ui/rocket.svg',
      failed: '/images/ui/red-rocket.svg'
    },
    initiation_email_sent: {
      pending: '/images/ui/gray-email.svg',
      // in_progress: '/images/ui/yellow-email.svg',
      in_progress: '/images/ui/black-email.svg',
      success: '/images/ui/green-email.svg',
      failed: '/images/ui/red-email.svg'
    },
    waiting_for_email: {
      pending: '/images/ui/gray-email.svg',
      // in_progress: '/images/ui/yellow-email.svg',
      in_progress: '/images/ui/black-email.svg',
      success: '/images/ui/green-email.svg',
      failed: '/images/ui/red-email.svg'
    },
    waiting_dealer_action: {
      pending: '/images/ui/gray-action.svg',
      // in_progress: '/images/ui/yellow-action.svg',
      in_progress: '/images/ui/black-action.svg',
      success: '/images/ui/green-action.svg',
      failed: '/images/ui/red-action.svg'
    },
    waiting_license_validation: {
      pending: '/images/ui/gray-file.svg',
      // in_progress: '/images/ui/yellow-file.svg',
      in_progress: '/images/ui/black-file.svg',
      success: '/images/ui/green-file.svg',
      failed: '/images/ui/red-file.svg'
    },
    outbound_transfer_confirmed: {
      pending: '/images/ui/gray-right.svg',
      // in_progress: '/images/ui/yellow-right.svg',
      in_progress: '/images/ui/black-right.svg',
      success: '/images/ui/green-right.svg',
      failed: '/images/ui/red-right.svg'
    },
    outbound_transfer_complete: {
      pending: '/images/ui/gray-right.svg',
      // in_progress: '/images/ui/yellow-right.svg',
      in_progress: '/images/ui/black-right.svg',
      success: '/images/ui/green-right.svg',
      failed: '/images/ui/red-right.svg'
    }
  };
  const macroStatus = stepImgs[step?.status]
  if (step?.status === TransferMacrostatus.waiting_dealer_action && step?.microStatus && step?.microStatus.length > 0) {
    const isOptedOut = step?.microStatus?.[0]?.status === TransferMicrostatus.opted_out;
    if (isOptedOut) {
      return macroStatus['failed']
    }
  }
  if (transferDetails && transferDetails?.subStatus && transferDetails?.subStatus === 'queue_status_on_hold' && step?.status === transferDetails.status) {
    return macroStatus['failed']
  }
  return macroStatus[step.stepStatus]
}

function getSchedulerColor(params: any): any {
  if (params?.timerStopped && !params?.timerExecuted) return "green"; // Timer stopped
  if (params?.timerExecuted && !params?.timerStopped) return "sub-red-box"; // Timer executed
  if (params?.schedulerId && !params?.isActive) return "sub-yellow-box"; // Not active

  if (params?.schedulerId && params?.isActive && params?.schedulerTimeout) {
    const startTime = new Date(params.createdAt).getTime();
    const endTime = new Date(params.schedulerTimeout).getTime();
    const now = new Date().getTime();

    const duration = endTime - startTime; // Total timer duration
    const elapsed = now - startTime; // Time passed
    const ratio = elapsed / duration; // Progress percentage

    if (ratio > 1) return "sub-red-box"; // More than 100% elapsed → Red
    if (ratio > 0.75) return "sub-yellow-box"; // More than 75% elapsed → Yellow
    return "sub-black-box"; // 75% or less elapsed → Black
  }

  return null;
}


export const getSubStatusColorClass = (step: any) => {
  if (step && step?.status) {
    const params = {
      schedulerId: step?.metaData?.schedulerId,
      isActive: step?.metaData?.active,
      schedulerTimeout: step?.metaData?.schedulerTimeout,
      createdAt: step?.createdAt,
      timerStopped: step?.metaData?.stopped_at,
      timerExecuted: step?.metaData?.executed_at
    }
    if (step?.status === TransferMicrostatus.email_bounced) {
      return 'sub-yellow-box';
    }
    if (step?.status === TransferMicrostatus.skip_step_acquired) {
      return 'sub-yellow-box';
    }
    if (step?.status === TransferMicrostatus.email_unreachable) {
      return 'sub-red-box';
    }
    
    if(step?.status === TransferMicrostatus.first_time_confirmation_delay) {
      if(step?.metaData?.executed_at) {
        return 'sub-green-box';
      }
      return 'sub-black-box'; 
    }
    const colorStatus = getSchedulerColor(params)
    if (colorStatus && colorStatus !== null) {
      return colorStatus && colorStatus == 'green' ? '' : colorStatus;
    }
    if (step?.status === transferDetailsMicroStatus[TransferMicrostatus.document_validated as keyType<typeof transferDetailsMicroStatus>] || step?.status === TransferMicrostatus.document_validated) {
      if (!step?.metaData?.status) {
        return 'sub-red-box';
      }
    }

    if (step?.status === TransferMicrostatus.ocr_checks) {
      const ocrCheckStatus = {
        in_progress: 'sub-black-box',
        failed: 'sub-red-box',
        passed: '',
        false: 'sub-red-box',
        true: ''
      };
      if (step?.metaData?.status && ocrCheckStatus[step?.metaData?.status as keyType<typeof ocrCheckStatus>]) {
        if (step?.metaData?.status === "failed" && step?.metaData?.code)
          return `${ocrCheckStatus[step?.metaData?.status as keyType<typeof ocrCheckStatus>]}`;
        if (["in_progress", "passed"].includes(step?.metaData?.status as keyType<typeof ocrCheckStatus>))
          return ocrCheckStatus[step?.metaData?.status as keyType<typeof ocrCheckStatus>];
      }
    }
    if (step?.status === TransferMicrostatus.ez_check) {
      const ezCheckStatus = {
        in_progress: 'sub-black-box',
        failed: 'sub-red-box',
        false: 'sub-red-box',
        passed: '',
        true: ''
      };
      if (step?.metaData && 'status' in step?.metaData && ezCheckStatus[step?.metaData?.status as keyType<typeof ezCheckStatus>]) {
        return ezCheckStatus[step?.metaData?.status as keyType<typeof ezCheckStatus>];
      }
    }

    if ((step?.status === TransferMicrostatus.manual_license_review) && (typeof step?.metaData?.status === "boolean")) {
      return !step?.metaData?.status ? 'sub-red-box' : '';
    }

    switch (step.status) {
      case TransferMicrostatus.in_progress:
        return 'sub-black-box';
      case TransferMicrostatus.in_hold:
        return 'sub-red-box';
      case TransferMicrostatus.opted_out:
        return 'sub-yellow-box';
      case TransferMicrostatus.dealer_documents_pending:
        return 'sub-yellow-box';
      case TransferMicrostatus.required_documents_pending:
        return 'sub-yellow-box';
      case TransferMicrostatus.dealer_action_pending:
        return 'sub-black-box';
      case TransferMicrostatus.email_bounced:
        return 'sub-yellow-box';
      default:
        return '';
    }
  }
  return '';
}

export const checkIsNFATransfer = (products: any[]) => {
  if (products && products.length > 0) {
    const isNFATransfer = products.find((product) => {
      return product === 'NFASuppressor';
    });
    return isNFATransfer ? true : false;
  }
  return false;
}

export const getSubStatus = (transferDetails: any) => {
  if (transferDetails && transferDetails?.TransferDetails && transferDetails?.TransferDetails?.subStatus) {
    let microStatus: any = transferDetails?.TransferDetails?.subStatus;
    const status = transferQueueSubStatusV2[microStatus as keyType<typeof transferQueueSubStatusV2>];
    return status ? status : null;
  }
  return null;
}

export const getCardColor = (step: any, transferDetails: any) => {
  if (transferDetails && transferDetails?.subStatus && transferDetails?.subStatus === 'queue_status_on_hold' && step?.status === transferDetails.status) {
    return 'red-step'
  }

  if (step?.status === TransferMacrostatus.waiting_dealer_action && step?.microStatus && step?.microStatus.length > 0) {
    const isOptedOut = step?.microStatus?.[0]?.status === TransferMicrostatus.opted_out;
    if (isOptedOut) {
      return 'red-step';
    }
  }
  
  switch (step?.stepStatus) {
    case 'pending':
      return 'in_active_step'
    case 'in_progress':
      return 'black-step'
    case 'failed':
      return 'red-step'
  }
}

export const getProgressStatusAndDateTime = (step: any, completedTime: any, statusDetails:any) => {
  const status = step?.status;
  const includePrefix = status &&
    (status === TransferMicrostatus.dealer_selected ||
      status === TransferMicrostatus.sms_sent_to_dealer ||
      status === TransferMicrostatus.email_acquired ||
      status === TransferMicrostatus.accepted_transfer ||
      status === TransferMicrostatus.manual_review_pending ||
      status === TransferMicrostatus.manual_license_review ||
      status === TransferMicrostatus.ready_for_outbound_processing ||
      status === TransferMicrostatus.email_available
    );
  const prefix = includePrefix ? 'on ' : ''

  if (status === TransferMicrostatus.sms_sent_to_dealer && step?.metaData && Object?.keys(step.metaData)?.includes('isSMSSent') && step.metaData.isSMSSent === false){
    return prefix + getFormattedDateTimeV2(step?.metaData?.schedulerTimeout);
  }

  const dateTime = prefix + getFormattedDateTimeV2(step.metaData && step.metaData?.timestamp ? step.metaData.timestamp : step.createdAt);
  if (step && step?.status === TransferMicrostatus.transfer_initiated && (step?.metaData && step?.metaData?.source)) {
    let source = Object.keys(TransferSourceEnum).includes(step?.metaData?.source) ? `${TransferSourceEnum[step?.metaData?.source as keyType<typeof TransferSourceEnum>]}` : step?.metaData?.source;
    source = statusDetails?.sourceDescription !== '' ? statusDetails?.sourceDescription : source;
    return `${dateTime} : ${source}`;
  }
  if (status &&
    (status === TransferMicrostatus.in_hold ||
      // status === TransferMicrostatus.email_available ||
      status === TransferMicrostatus.waiting_for_admin ||
      status === TransferMicrostatus.dealer_action_pending ||
      status === TransferMicrostatus.waiting_for_customer_action ||
      status === TransferMicrostatus.waiting_for_agent_action ||
      status === TransferMicrostatus.email_reminder_sent ||
      status === TransferMicrostatus.dealer_documents_pending
    )) {
    return '';
  }
  const emailStatus = [TransferMicrostatus.resent_email, TransferMicrostatus.email_delivered, TransferMicrostatus.email_opened, TransferMicrostatus.email_clicked, TransferMicrostatus.email_bounced, TransferMicrostatus.email_unreachable];
  if (emailStatus.includes(status)) {
    if(status === TransferMicrostatus.email_bounced){
      if(step?.metaData?.retry === 1 || step?.metaData?.type === 'completion'){
        return  `on: ${dateTime} - Bounced`;
      }
      return  `on: ${dateTime} - Retry`;
    }
    if(status === TransferMicrostatus.email_unreachable){
      return `on: ${dateTime}`;
    }
    return `on: ${dateTime} - ${transferDetailsMicroStatus[status as keyType<typeof transferDetailsMicroStatus>]}`;
  }

  if (status && status === TransferMicrostatus.document_validated) {
    if(step?.metaData?.documentType.toLowerCase() === 'cfd') return dateTime
    if (step?.metaData?.status)
      return `${dateTime} - Passed`
    else return `${dateTime} - Failed`;
  }

  if (status && status === TransferMicrostatus.ocr_checks) {
    if (step?.metaData?.status) {
      switch (step?.metaData?.status) {
        case 'failed':
          return `${dateTime} - Failed`;
        case 'in_progress':
          return `${dateTime} - In Progress`;
        case 'passed':
          return `${dateTime} - Passed`;
      }
    }
  }
  if (status && status === TransferMicrostatus.in_progress) {
    if (step?.createdAt) {
      const createdDate = new Date(step?.createdAt);
      const currentDate = new Date();

      let timeDiff;
      if (completedTime && completedTime !== null) {
        const completedDate = new Date(completedTime);
        timeDiff = completedDate.getTime() - createdDate.getTime();
      } else {
        timeDiff = currentDate.getTime() - createdDate.getTime();
      }
      const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(timeDiff / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff / (1000 * 60)) % 60);
      const seconds = Math.floor((timeDiff / 1000) % 60);

      if (days > 0) {
        return `: ${days}d`;
      } else if (hours > 0) {
        return `: ${hours}h ${minutes}m`;
      } else if (minutes > 0) {
        return `: ${minutes}m ${seconds}s`;
      } else {
        return `: ${seconds}s`;
      }
    }

  }
  return dateTime;
}

export const checkIsTransferCompleted = (transferStatusWorkflow: any[]) => {
  if (transferStatusWorkflow && transferStatusWorkflow?.length > 0) {
    console.log('transferStatusWorkflow ?????????/', transferStatusWorkflow);
    const outbound: any = transferStatusWorkflow.find((workflow: any) => {
      return workflow?.status === 'outbound_transfer_complete';
    })
    if (outbound) {
      return outbound.stepStatus === 'success'
    }
    return false
  }
  return false
}

export const getStartAndCompleteDate = (transferStatusWorkflow: any[]) => {
  const initiated = transferStatusWorkflow.find((status: any) => status.status === TransferMacrostatus.transfer_initiated);
  const sDate = initiated && initiated?.startedAt ? initiated?.startedAt : null;
  const completed = transferStatusWorkflow.find((status: any) => status.status === TransferMacrostatus.outbound_transfer_complete);
  const eDate = completed && completed?.finishedAt ? completed?.finishedAt : null;
  return {
    sDate,
    eDate
  }
}

export const isDocInReview = (stepMetaData: any, transferStatusWorkflow: any[]) => {
  if (stepMetaData && stepMetaData?.documentType && transferStatusWorkflow && transferStatusWorkflow?.length > 0) {
    const documentProcessing = transferStatusWorkflow.find((workflow: any) => workflow?.status === TransferMacrostatus.waiting_license_validation)
    if (documentProcessing && documentProcessing?.microStatus && documentProcessing?.microStatus.length > 0) {
      const currentStepInReview = documentProcessing?.microStatus.find((microStatus: any) => {
        return microStatus?.status === TransferMicrostatus.manual_review_pending && microStatus?.metaData?.documentType === stepMetaData?.documentType
      })
      return currentStepInReview && currentStepInReview?.status
    }
    return true
  }
  return true
}

export const TransferFilterModelInitialValue = {
  agent: { filterType: "set", values: [] }, firearm: { filterType: "set", values: [] }, storeName: { filterType: "set", values: [] }, source: { filterType: "set", values: [] },ecommerce_status: { filterType: "set", values: [] }, transferType: { filterType: "set", values: [] }, derivedStatus: { filterType: "set", values: [] }, "licensee.id": { filterType: "text", type: "equals", filter: "" }, referenceId: { filterType: "text", type: "contains", filter: "" }, sellerLicenseKey: { filterType: "text", type: "contains", filter: "" }, searchText: { filterType: "text", type: "contains", filter: "" }, microStatus: { filterType: "set", values: [] }, createdAt: { filterType: "date", type: "inRange", dateFrom: "", dateTo: "" }, is_external: {
    filterType: 'set', values: []
  }, isArchive: { filterType: 'set', values: [] }, linkedTransfers: { filterType: 'set', values: [] }
}
export const InitialPermissionsTabList:any = {
  transfer: [],
  dealerSearch: [],
  admin: []
}
  